import { z } from "zod";
import {
	isoCountryCodeSchema,
	optionalIsoDateStringSchema,
	sanitizedLongTextSchema,
	sanitizedNameSchema,
} from "../validation";

/** Construction-site address, mirroring the billing-address field structure
 * (line1/line2/city/zip/country). All members optional. */
export const projectSiteAddressSchema = z
	.object({
		line1: sanitizedLongTextSchema(255),
		line2: sanitizedLongTextSchema(255),
		city: sanitizedLongTextSchema(120),
		zip: sanitizedLongTextSchema(32),
		country: isoCountryCodeSchema.optional(),
	})
	.strict();

export type ProjectSiteAddress = z.infer<typeof projectSiteAddressSchema>;

/**
 * Project lifecycle phases (PLA-439). Single source of truth — web and API
 * derive option lists, defaults and validation from this tuple. Order is the
 * display order; labels live in the `projects` i18n namespace (`phases.<key>`).
 */
export const PROJECT_PHASES = [
	"preparation",
	"quantity_takeoff",
	"execution",
	"commissioning_handover",
	"maintenance",
] as const;

export type ProjectPhase = (typeof PROJECT_PHASES)[number];

export const DEFAULT_PROJECT_PHASE: ProjectPhase = "preparation";

/** Output/type schema — only the current phase keys. */
export const projectPhaseSchema = z.enum(PROJECT_PHASES);

/**
 * Pre-PLA-439 phase keys. Accepted on input only, for the mixed-version
 * rollout window (old browser tabs). Mirrored by the SQL CASE in migration
 * 0143. Remove together with the DB trigger in the follow-up release.
 */
export const LEGACY_PROJECT_PHASES = [
	"initiation",
	"design_planning",
	"pre_construction",
	"construction",
	"monitoring_controlling",
	"operation_maintenance",
] as const;

export type LegacyProjectPhase = (typeof LEGACY_PROJECT_PHASES)[number];

export const LEGACY_PHASE_REMAP: Record<LegacyProjectPhase, ProjectPhase> = {
	initiation: "preparation",
	design_planning: "preparation",
	pre_construction: "quantity_takeoff",
	construction: "execution",
	monitoring_controlling: "execution",
	operation_maintenance: "maintenance",
};

const isLegacyProjectPhase = (value: string): value is LegacyProjectPhase =>
	value in LEGACY_PHASE_REMAP;

export const normalizeProjectPhase = (
	value: ProjectPhase | LegacyProjectPhase,
): ProjectPhase =>
	isLegacyProjectPhase(value) ? LEGACY_PHASE_REMAP[value] : value;

/** Input schema — accepts current + legacy keys, always yields a current key. */
export const projectPhaseInputSchema = z
	.enum([...PROJECT_PHASES, ...LEGACY_PROJECT_PHASES])
	.transform(normalizeProjectPhase);

export const createProjectInputSchema = z
	.object({
		/**
		 * Explicit org scope. Required (PLA-302).
		 */
		orgId: z.string().uuid(),
		name: sanitizedNameSchema({ max: 255 }),
		description: sanitizedLongTextSchema(1024),
		/** Client the org runs the project for — informational free text. */
		customerName: sanitizedLongTextSchema(255),
		/** Physical construction-site address — same structure as the billing
		 *  address so future address-search / mapping integrations can consume
		 *  it directly. */
		siteAddress: projectSiteAddressSchema.optional(),
		startDate: optionalIsoDateStringSchema,
		endDate: optionalIsoDateStringSchema,
		phase: projectPhaseInputSchema.optional(),
		projectManagerUserId: z.string().uuid(),
	})
	.strict();

export const updateProjectInputSchema = createProjectInputSchema
	.omit({ projectManagerUserId: true })
	.partial()
	.extend({
		id: z.string().uuid(),
		// Nullable on update: `undefined` means "leave unchanged" (Drizzle drops
		// undefined set-fields), so clearing a stored value requires an explicit
		// null from the form.
		description: sanitizedLongTextSchema(1024).nullable().optional(),
		customerName: sanitizedLongTextSchema(255).nullable().optional(),
	})
	.strict();

export type CreateProjectInput = z.infer<typeof createProjectInputSchema>;
export type UpdateProjectInput = z.infer<typeof updateProjectInputSchema>;
